import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useIntl, defineMessages } from 'react-intl';
import { Box } from '@lifeomic/chroma-react/components/Box';
import { Text } from '@lifeomic/chroma-react/components/Text';
import { Link } from '@lifeomic/chroma-react/components/Link';
import {
  Tab,
  Tabs,
  TabList,
  TabPanel,
} from '@lifeomic/chroma-react/components/Tabs';
import { makeStyles } from '@lifeomic/chroma-react/styles';
import { useParams, Redirect } from 'react-router-dom';
import {
  useActiveProjectId,
  useFeatures,
} from '@lifeomic/phc-web-toolkit/src/hooks';
import { useNotify } from '@lifeomic/phc-web-toolkit/src/hooks/useNotify';
import {
  apiHooks,
  type PhcPlatformRestEndpoints,
} from '@lifeomic/phc-web-toolkit/src/hooks/useRestAPIHooks';
import urls from '../../../../util/urls';
import {
  getFriendlyAction,
  getFriendlyChannel,
} from '../../AIRules/displayStrings';
import AuditMasterDetail from '../../AIRules/components/AuditMasterDetail';
import { AuditListCell } from '../../AIRules/components/AuditListCell';
import { RuleActionAuditRecord } from '../../AIRules/types';
import {
  formatDateTime,
  getAuditSubjectMemberId,
  normalizeJson,
} from '../../AIRules/utils';
import MemberDetail from './index';
import { getMemberRuleActions } from './RuleActionAudit.utils';
import { DocumentExtractionV2Tasks } from './DocumentExtractionV2Tasks';
import { ZoriPulseScanTasks } from './ZoriPulseScanTasks';
import { memberDetailMessages } from './messages';

const messages = defineMessages({
  emptyRuleActions: {
    id: 'MemberDetail.ruleActionAudit.emptyRuleActions',
    defaultMessage: 'No rule actions found for this member',
  },
  errorLoadingRuleActions: {
    id: 'MemberDetail.ruleActionAudit.errorLoadingRuleActions',
    defaultMessage: 'Error loading rule action audit',
  },
  emptyMemberNotifications: {
    id: 'MemberDetail.ruleActionAudit.emptyMemberNotifications',
    defaultMessage: 'No member notifications found for this member',
  },
  errorLoadingMemberNotifications: {
    id: 'MemberDetail.ruleActionAudit.errorLoadingMemberNotifications',
    defaultMessage: 'Error loading member notification audit',
  },
  selectRowHint: {
    id: 'MemberDetail.ruleActionAudit.selectRowHint',
    defaultMessage: 'Select a row to view details',
  },
});

type AuditTab =
  | 'rule-actions'
  | 'extraction-tasks'
  | 'pulse-tasks'
  | 'member-notifications';
type MemberNotificationAuditRecord =
  PhcPlatformRestEndpoints['GET /v1/member-notification-service/notifications/audit']['Response']['items'][number];

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minHeight: 0,
    overflow: 'hidden',
  },
  tabListWrapper: {
    width: '100%',
    flexShrink: 0,
    backgroundColor: theme.palette.background.paper,
    marginBottom: theme.spacing(1),
    padding: theme.spacing(1),
  },
  tabList: {
    flexShrink: 0,
    alignSelf: 'flex-start',
    display: 'inline-flex',
  },
  tabPanel: {
    flex: 1,
    minHeight: 0,
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: theme.palette.background.paper,
    position: 'relative',
  },
  extractionTasksPanel: {
    padding: theme.spacing(2),
    overflow: 'auto',
  },
}));

type RuleActionAuditContentProps = {
  memberId: string;
};

const formatBoolean = (value: boolean) => (value ? 'Yes' : 'No');

const formatList = (value: string[] | undefined) => value?.join('\n') || '';

export const RuleActionAuditContent: React.FC<RuleActionAuditContentProps> = ({
  memberId,
}) => {
  const classes = useStyles({});
  const { formatMessage } = useIntl();
  const [notify] = useNotify();
  const activeProjectId = useActiveProjectId();
  const lastNotifiedError = useRef<string | null>(null);
  const lastNotifiedNotificationError = useRef<string | null>(null);
  const [selectedAuditTab, setSelectedAuditTab] =
    useState<AuditTab>('rule-actions');
  const [selectedRuleActionId, setSelectedRuleActionId] = useState<
    string | null
  >(null);
  const [selectedNotificationAuditId, setSelectedNotificationAuditId] =
    useState<string | null>(null);

  const ruleActionsQuery = apiHooks.useAPIQuery(
    'GET /v1/agents-v2/zori-audit/rule-actions',
    {
      limit: 200,
    },
    {
      staleTime: 0,
      refetchOnWindowFocus: false,
      enabled: Boolean(memberId),
    },
  );

  const memberNotificationsQuery = apiHooks.useAPIQuery(
    'GET /v1/member-notification-service/notifications/audit',
    {
      patientId: memberId,
      projectId: activeProjectId,
      limit: 100,
    },
    {
      staleTime: 0,
      refetchOnWindowFocus: false,
      enabled:
        Boolean(memberId) &&
        Boolean(activeProjectId) &&
        selectedAuditTab === 'member-notifications',
    },
  );

  useEffect(() => {
    const error = ruleActionsQuery.error;
    const message =
      error instanceof Error
        ? error.message
        : typeof error === 'string'
        ? error
        : null;
    if (!message) {
      lastNotifiedError.current = null;
      return;
    }
    if (lastNotifiedError.current !== message) {
      lastNotifiedError.current = message;
      notify(formatMessage(messages.errorLoadingRuleActions), 'error');
    }
  }, [formatMessage, notify, ruleActionsQuery.error]);

  useEffect(() => {
    const error = memberNotificationsQuery.error;
    const message =
      error instanceof Error
        ? error.message
        : typeof error === 'string'
        ? error
        : null;
    if (!message) {
      lastNotifiedNotificationError.current = null;
      return;
    }
    if (lastNotifiedNotificationError.current !== message) {
      lastNotifiedNotificationError.current = message;
      notify(formatMessage(messages.errorLoadingMemberNotifications), 'error');
    }
  }, [formatMessage, memberNotificationsQuery.error, notify]);

  const memberRuleActions = useMemo(
    () => getMemberRuleActions(ruleActionsQuery.data || [], memberId),
    [ruleActionsQuery.data, memberId],
  );

  const rowId = (row: RuleActionAuditRecord) =>
    `${row.timestamp}:${row.subject_id}:${row.rule_id || ''}:${row.action}`;

  const notificationRowId = (row: MemberNotificationAuditRecord) =>
    `${row.createdAt}:${row.messageId}`;

  const listColumns = useMemo(
    () => [
      {
        header: { label: 'Rule Action' },
        cell: {
          content: (row: RuleActionAuditRecord) => (
            <AuditListCell
              primary={getFriendlyAction(row.action)}
              secondary={getFriendlyChannel(row.channel)}
              timestamp={formatDateTime(row.timestamp)}
            />
          ),
        },
      },
    ],
    [],
  );

  const notificationListColumns = useMemo(
    () => [
      {
        header: { label: 'Notification' },
        cell: {
          content: (row: MemberNotificationAuditRecord) => (
            <AuditListCell
              primary={row.templateName}
              secondary={row.status}
              timestamp={formatDateTime(row.createdAt)}
            />
          ),
        },
      },
    ],
    [],
  );

  const notificationAuditRows = memberNotificationsQuery.data?.items ?? [];

  return (
    <Box className={classes.root}>
      <Tabs selectedTabId={selectedAuditTab} variant="pill">
        <Box className={classes.tabListWrapper}>
          <TabList
            aria-label="Rule actions audit sections"
            className={classes.tabList}
          >
            <Tab
              stopId="rule-actions"
              onClick={() => setSelectedAuditTab('rule-actions')}
            >
              {formatMessage(
                memberDetailMessages.memberRuleActionAuditRuleActionsTab,
              )}
            </Tab>
            <Tab
              stopId="extraction-tasks"
              onClick={() => setSelectedAuditTab('extraction-tasks')}
            >
              {formatMessage(
                memberDetailMessages.memberRuleActionAuditExtractionTasksTab,
              )}
            </Tab>
            <Tab
              stopId="pulse-tasks"
              onClick={() => setSelectedAuditTab('pulse-tasks')}
            >
              {formatMessage(
                memberDetailMessages.memberRuleActionAuditPulseTasksTab,
              )}
            </Tab>
            <Tab
              stopId="member-notifications"
              onClick={() => setSelectedAuditTab('member-notifications')}
            >
              {formatMessage(
                memberDetailMessages.memberRuleActionAuditNotificationsTab,
              )}
            </Tab>
          </TabList>
        </Box>
        <TabPanel stopId="rule-actions" className={classes.tabPanel}>
          <AuditMasterDetail
            rows={memberRuleActions}
            loading={ruleActionsQuery.isLoading}
            emptyMessage={formatMessage(messages.emptyRuleActions)}
            noSelectionMessage={formatMessage(messages.selectRowHint)}
            rowId={rowId}
            listColumns={listColumns}
            detailTitle={(row: RuleActionAuditRecord) =>
              row.rule_id
                ? `${row.rule_id} - ${getFriendlyAction(row.action)}`
                : getFriendlyAction(row.action)
            }
            detailRows={(row: RuleActionAuditRecord) => [
              {
                label: 'Timestamp',
                value: formatDateTime(row.timestamp),
              },
              {
                label: 'Rule',
                value: row.rule_id ? (
                  <Text size="table">
                    <Link
                      to={`${urls.app.workflows.zoriRules(
                        'all',
                      )}?rule=${encodeURIComponent(row.rule_id)}`}
                    >
                      {row.rule_id}
                    </Link>
                  </Text>
                ) : (
                  ''
                ),
              },
              { label: 'Action', value: getFriendlyAction(row.action) },
              {
                label: 'Channel',
                value: getFriendlyChannel(row.channel),
              },
              { label: 'Category', value: row.category || '' },
              { label: 'Status', value: row.status },
              {
                label: 'Subject',
                value: (
                  <Text size="table">
                    <Link
                      to={urls.app.members(
                        getAuditSubjectMemberId(row.subject_id),
                      )}
                    >
                      {getAuditSubjectMemberId(row.subject_id)}
                    </Link>
                  </Text>
                ),
              },
              {
                label: 'Langfuse Trace',
                value: row.trace_id || '',
              },
              {
                label: 'Explain Decision',
                value:
                  row.explain_decision_output ||
                  row.action_params?.explain_decision_output ||
                  '',
              },
              {
                label: 'Action Params',
                value: normalizeJson(row.action_params || {}),
              },
              { label: 'TTL', value: row.ttl ? String(row.ttl) : '' },
            ]}
            selectedRowId={selectedRuleActionId}
            setSelectedRowId={setSelectedRuleActionId}
          />
        </TabPanel>
        <TabPanel
          stopId="extraction-tasks"
          className={`${classes.tabPanel} ${classes.extractionTasksPanel}`}
        >
          {selectedAuditTab === 'extraction-tasks' ? (
            <DocumentExtractionV2Tasks subjectId={memberId} />
          ) : null}
        </TabPanel>
        <TabPanel stopId="pulse-tasks" className={classes.tabPanel}>
          {selectedAuditTab === 'pulse-tasks' ? (
            <ZoriPulseScanTasks subjectId={memberId} />
          ) : null}
        </TabPanel>
        <TabPanel stopId="member-notifications" className={classes.tabPanel}>
          <AuditMasterDetail
            rows={notificationAuditRows}
            loading={memberNotificationsQuery.isLoading}
            emptyMessage={formatMessage(messages.emptyMemberNotifications)}
            noSelectionMessage={formatMessage(messages.selectRowHint)}
            rowId={notificationRowId}
            listColumns={notificationListColumns}
            detailTitle={(row: MemberNotificationAuditRecord) =>
              `${row.templateName} - ${row.status}`
            }
            detailRows={(row: MemberNotificationAuditRecord) => [
              {
                label: 'Created',
                value: formatDateTime(row.createdAt),
              },
              { label: 'Template', value: row.templateName },
              { label: 'Status', value: row.status },
              { label: 'Email Sent', value: formatBoolean(row.emailSent) },
              { label: 'SMS Sent', value: formatBoolean(row.smsSent) },
              {
                label: 'Resource',
                value:
                  row.resourceType && row.resourceId
                    ? `${row.resourceType}/${row.resourceId}`
                    : row.resourceId || '',
              },
              { label: 'Message ID', value: row.messageId },
              { label: 'Project ID', value: row.projectId || '' },
              { label: 'Survey IDs', value: formatList(row.surveyIds) },
              {
                label: 'Suppression Reason',
                value: row.suppressionReason || '',
              },
              { label: 'Info', value: formatList(row.info) },
              { label: 'Errors', value: formatList(row.errors) },
              { label: 'Request ID', value: row.requestId || '' },
              { label: 'Correlation ID', value: row.correlationId || '' },
              {
                label: 'Incoming Correlation ID',
                value: row.incomingCorrelationId || '',
              },
              { label: 'Caller User', value: row.callerUser || '' },
              {
                label: 'Caller User Agent',
                value: row.callerUserAgent || '',
              },
            ]}
            selectedRowId={selectedNotificationAuditId}
            setSelectedRowId={setSelectedNotificationAuditId}
          />
        </TabPanel>
      </Tabs>
    </Box>
  );
};

const MemberRuleActionAudit: React.FC = () => {
  const hasFeature = useFeatures();
  const { memberId } = useParams<{ memberId: string }>();

  if (!hasFeature('fountainLifePowerUser')) {
    return <Redirect to={`${urls.app.members(memberId)}/annual-report`} />;
  }

  return <MemberDetail section="rule-action-audit" />;
};

MemberRuleActionAudit.displayName = 'MemberRuleActionAudit';
export default MemberRuleActionAudit;
